import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { I18nProvider } from "@/context/I18nContext";
import { StoreLayout } from "@/components/layout/StoreLayout";
import { ProtectedRoute } from "@/pages/admin/ProtectedRoute";

import Home from "@/pages/Home";
import CategoryPage from "@/pages/CategoryPage";
import BrandPage from "@/pages/BrandPage";
import ProductDetail from "@/pages/ProductDetail";
import LandingPage from "@/pages/LandingPage";
import Concepts from "@/pages/Concepts";
import StaticPage from "@/pages/StaticPage";
import CareGuide from "@/pages/CareGuide";
import SizeGuide from "@/pages/SizeGuide";
import NotFound from "@/pages/NotFound";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import { LANDING_SLUGS } from "@/lib/landingPages";
import { LANG_PREFIXES } from "@/lib/i18nRoutes";

const Store = ({ children }) => <StoreLayout>{children}</StoreLayout>;

// Store route definitions (relative paths, no leading slash). Rendered once at
// the root (English / x-default) and once under each language prefix.
const STORE_ROUTES = [
  { path: "", el: <Home /> },
  { path: "collection/:slug", el: <CategoryPage /> },
  { path: "brand/:slug", el: <BrandPage /> },
  { path: "product/:handle", el: <ProductDetail /> },
  ...LANDING_SLUGS.map((s) => ({ path: s, el: <LandingPage slug={s} /> })),
  { path: "shipping", el: <StaticPage slug="shipping" /> },
  { path: "returns", el: <StaticPage slug="returns" /> },
  { path: "size-guide", el: <SizeGuide /> },
  { path: "care-guide", el: <CareGuide /> },
  { path: "about", el: <StaticPage slug="about" /> },
  { path: "contact", el: <StaticPage slug="contact" /> },
];

const buildPath = (prefix, p) => {
  const base = prefix ? `/${prefix}` : "";
  return p ? `${base}/${p}` : base || "/";
};

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <I18nProvider>
          <AuthProvider>
            <CartProvider>
              <Routes>
                {/* English (x-default) at root */}
                {STORE_ROUTES.map((r) => (
                  <Route key={`en-${r.path}`} path={buildPath("", r.path)} element={<Store>{r.el}</Store>} />
                ))}
                {/* Language-prefixed routes */}
                {LANG_PREFIXES.map((lng) =>
                  STORE_ROUTES.map((r) => (
                    <Route key={`${lng}-${r.path}`} path={buildPath(lng, r.path)} element={<Store>{r.el}</Store>} />
                  ))
                )}
                {/* Non-localized utility routes */}
                <Route path="/concepts" element={<Concepts />} />
                <Route path="/loginmanage/login" element={<AdminLogin />} />
                <Route path="/loginmanage" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
                <Route path="*" element={<Store><NotFound /></Store>} />
              </Routes>
              <Toaster position="top-center" richColors />
            </CartProvider>
          </AuthProvider>
        </I18nProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
